import { Component, Input, OnInit } from '@angular/core';
import { NgForm }                   from '@angular/forms';
import { TranslateService }         from '@ngx-translate/core';
import { UIRouter }                 from '@uirouter/core';

import bntIcon from 'icons/bnt.svg';

import { BntCredentialsModel }      from './models/bnt.credentials';
import { BntService }               from './bnt.service';
import { CodeTypeModel }            from '../core/models/code-type';
import { CompanyIntegrationsModel } from '../companies/models/company.integrations';
import { CompanyService }           from '../companies/company.service';
import { MessagesService }          from '../messages/messages.service';
import { VendorModel }              from '../vendors/models/vendor';
import { VendorSearchService }      from '../vendors/vendor.search.service';

@Component({
  selector   : 'wor-bnt-settings',
  template: require('./bnt.settings.component.html')
})
export class BntSettingsComponent implements OnInit {
  @Input() credentials: BntCredentialsModel;
  @Input() session    : any;
  @Input() settings   : CompanyIntegrationsModel;

  public bntIcon : string = bntIcon;
  public title   : string = this.bntService.title;

  /**
   * The password is never returned by the API, so this is left blank and is
   * only sent when the user types a new one.
   */
  public password = '';

  public passwordPlaceholder = '';
  public savedEmail          : string = '';
  public vendor  : VendorModel;

  public priceOptions : Array<CodeTypeModel> = [
    {
      code          : 'NOTHING',
      description   : 'Dont change the price',
      language_label: this.translateService.instant('JS_SPACE.BNT.DONT_CHANGE_THE_PRICE')
    },
    {
      code          : 'DIFFERENT',
      description   : 'Update to BNT\'s price, if price is different',
      language_label: this.translateService.instant('JS_SPACE.BNT.PRICE_IS_DIFFERENT')
    },
    {
      code          : 'HIGHER',
      description   : 'Update to BNT\'s price, if price is higher in BNT',
      language_label: this.translateService.instant('JS_SPACE.BNT.PRICE_HIGHER')
    },
    {
      code          : 'ALL',
      description   : 'Always use BNT\'s price',
      language_label: this.translateService.instant('JS_SPACE.BNT.ALWAYS_USE_BNT_PRICE')
    }
  ];

  constructor (
    private bntService         : BntService,
    public companyService      : CompanyService,
    private messagesService    : MessagesService,
    private router             : UIRouter,
    private translateService   : TranslateService,
    private vendorSearchService: VendorSearchService
  ) {}

  activate () : void {
    this.bntService.activate()
    .then(() => {
      this.messagesService.show(this.translateService.instant('JS_SPACE.INTEGRATIONS.ACTIVATED', {
        name: this.title
      }), 'success');

      this.router.stateService.reload();
    })
    .catch(err => {
      this.messagesService.error(err);
    });
  }

  deactivate () : void {
    this.bntService.deactivate()
    .then(() => {
      this.messagesService.show(this.translateService.instant('JS_SPACE.MESSAGES.REMOVED_INTEGRATION', {
        name: this.title
      }), 'success');

      this.router.stateService.reload();
    })
    .catch(err => {
      this.messagesService.error(err);
    });
  }

  getVendor () : void {
    this.vendorSearchService.get(this.settings.bnt_ezyparts_vendor_id)
    .then(( vendor : VendorModel ) => {
      this.vendor = vendor;
    });
  }

  handleVendorChange ( vendor : string | VendorModel ) : void {
    if (!vendor) {
      this.settings.bnt_ezyparts_vendor_id = '';
    }
  }

  ngOnInit () : void {
    this.credentials = this.credentials || {
      bnt_ezyparts_email       : '',
      bnt_ezyparts_password_set: false
    };

    // The API sends null when no login is saved; compare like with like on save.
    this.credentials.bnt_ezyparts_email = this.credentials.bnt_ezyparts_email || '';
    this.savedEmail                     = this.credentials.bnt_ezyparts_email;

    if ( this.credentials.bnt_ezyparts_password_set ) {
      this.passwordPlaceholder = this.translateService.instant('COMPANY_SPACE.INTEGRATIONS.BNT.PASSWORD_ON_FILE');
    }

    if ( this.settings.bnt_ezyparts_vendor_id ) {
      this.getVendor();
    }
  }

  save ( form : NgForm ) : void {
    // Only admins may change the shared login, so it is sent only when it was edited.
    const loginChanged : boolean = !!this.password || this.credentials.bnt_ezyparts_email !== this.savedEmail;
    const saveLogin    : Promise<any> = loginChanged
      ? Promise.resolve(this.bntService.saveCredentials(this.credentials, this.password))
      : Promise.resolve(null);

    saveLogin
    .then(() => this.bntService.save(this.settings))
    .then(() => {
      this.messagesService.show(this.translateService.instant('JS_SPACE.MESSAGES.SAVED.SETTINGS'), 'success');

      form.form.markAsPristine();
      this.router.stateService.reload();
    })
    .catch(err => {
      this.messagesService.error(err);
    });
  }

  setVendorID ( vendor : VendorModel ) : void {
    this.settings.bnt_ezyparts_vendor_id = vendor.id;
  }
}
